import React, { useMemo, useState } from 'react';
import {
    Button,
    HeadlineColorsMap,
    HexMap,
    LinkWrapper,
    MilanBackgroundColors,
    MilanGradientColors,
    MilanHeadlineColors,
    MilanTextColors,
    S18GraphicHeadline,
    Typography,
    useCheckWidth,
} from '@milanlaser/mlcomponents';
import { getPaddingClasses, PaddingSize } from '../../../utils/builderPaddingOptions';
import VideoBadge from './VideoBadge';
import { GradientsColorsMap } from '../../../utils/builderColorOptions';
import { getVideoSources } from '../../../utils/builder.utils';
import { useFirebaseMetrics } from '../../../utils/useFirebaseMetrics';
import clsx from 'clsx';
import '../S18Video/S18Video.css';
interface S18VideoArgs {
    desktopPaddingTop: PaddingSize;
    desktopPaddingBottom: PaddingSize;
    mobilePaddingTop: PaddingSize;
    mobilePaddingBottom: PaddingSize;
    desktopVideo: string;
    backgroundGradient: MilanGradientColors | 'None';
    backgroundColor?: MilanBackgroundColors | 'None';
    children?: string;
    variant?: 'primary' | 'secondary' | 'cta' | 'overlay';
    buttonUrl?: string;
    name?: string;
    href?: string;
    imageSrcBadge?: string;
    imageAltBadge?: string;
    mobileVideo?: string;
    linkColor?: MilanTextColors;
    textColor?: MilanTextColors;
    text?: string;
    graphicsHeadlineText?: string;
    graphicsHeadlineUnderlinedText?: string;
    headlineSuffix?: string;
    mobileUnderlinedLines?: string[];
    graphicsHeadlineColor?: MilanHeadlineColors;
    preHeadlineText?: string;
    preHeadlineTextColor?: MilanTextColors;
    /**
     * Render `text` above the headline instead of between the headline and the CTA.
     * Opt-in so Builder-authored pages keep the original order.
     */
    textAboveHeadline?: boolean;
}

const S18VideoBackground = ({
    desktopVideo,
    buttonUrl,
    href: link,
    name: linkText,
    linkColor = 'TrueWhite',
    text,
    textColor = 'TrueWhite',
    variant,
    children,
    graphicsHeadlineColor = 'White',
    graphicsHeadlineText = '',
    graphicsHeadlineUnderlinedText = '',
    headlineSuffix = '',
    mobileUnderlinedLines,
    imageSrcBadge = 'https://cdn.builder.io/api/v1/file/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F3bd386d730254ea990ede4bd188d408d',
    imageAltBadge,
    backgroundGradient,
    backgroundColor = 'WhiteOff',
    desktopPaddingTop,
    desktopPaddingBottom,
    mobilePaddingTop,
    mobilePaddingBottom,
    preHeadlineText,
    preHeadlineTextColor = 'TrueWhite',
    textAboveHeadline = false,
}: S18VideoArgs) => {
    const [videoLoaded, setVideoLoaded] = useState(false);
    const { replaceVariables } = useFirebaseMetrics();
    const { isDesktop } = useCheckWidth();

    const processedText = replaceVariables(text || '');

    const mappedBgColor = HexMap[backgroundColor as MilanBackgroundColors] || 'transparent';
    const gradientMappedBgColor = GradientsColorsMap[backgroundGradient as MilanGradientColors] || 'transparent';

    const paddingTopAndBottom = getPaddingClasses(
        mobilePaddingTop,
        desktopPaddingTop,
        mobilePaddingBottom,
        desktopPaddingBottom
    );

    const paddingClassesWithResponsive = useMemo(() => {
        return clsx('s18VideoContainer', 'responsive-padding', paddingTopAndBottom, 'lg:px-4');
    }, [paddingTopAndBottom]);

    const getBgColor = useMemo(() => {
        if (backgroundGradient !== 'None') {
            if (gradientMappedBgColor !== 'transparent') {
                return { backgroundImage: gradientMappedBgColor };
            }
            return {};
        } else if (backgroundColor !== 'None') {
            return { backgroundColor: mappedBgColor };
        }
        return {};
    }, [backgroundGradient, backgroundColor]);

    // Same element and styling wherever it lands — only the position moves, so the
    // copy keeps the treatment it has today.
    const textContent = (
        <Typography tag="p" color={`${textColor}`} className="px-3 text-center m-0">
            {processedText}
        </Typography>
    );

    return (
        <div className={paddingClassesWithResponsive} style={{ ...getBgColor }}>
            <section className={`relative w-full max-w-7xl mx-auto`}>
                <div className="relative w-full aspect-[4/5] lg:aspect-[16/9] overflow-hidden lg:rounded-[30px]">
                    <div className="pointer-events-none absolute inset-0 z-10 bg-[#2e2e2e] opacity-75 lg:rounded-[30px] max-w-7xl mx-auto" />
                    <div className="max-w-7xl mx-auto relative h-full">
                        <video
                            id="bgvid1"
                            autoPlay
                            loop
                            muted
                            playsInline
                            poster="https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fd8df5c7b303c41d29290e2180d540bd3"
                            className="w-full h-full aspect-[4/5] lg:aspect-video object-cover lg:rounded-[30px]"
                            // Add muted as an attribute
                            onPlay={() => setVideoLoaded(true)}
                        >
                            {getVideoSources(desktopVideo).map((source, index) => (
                                <source key={index} src={source.src} type={source.type} />
                            ))}
                        </video>
                    </div>
                    {/* Overlay Content Start */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center max-sm:gap-5 gap-7 z-20 text-center mcl-px-4">
                        {/* Expiry line, when it leads the block. Guarded so blank copy
                            doesn't leave an empty paragraph holding open a gap. */}
                        {textAboveHeadline && processedText && textContent}
                        {/* Pre-Headline Text and Graphics Headline */}
                        <div className="flex flex-col items-center gap-2">
                            {preHeadlineText && (
                                <Typography tag="p" color={preHeadlineTextColor} className="m-0 mt-10">
                                    {preHeadlineText}
                                </Typography>
                            )}
                            {isDesktop || !mobileUnderlinedLines?.length ? (
                                <S18GraphicHeadline
                                    text={
                                        headlineSuffix
                                            ? `${graphicsHeadlineUnderlinedText} ${headlineSuffix}`
                                            : graphicsHeadlineText
                                    }
                                    underlined={graphicsHeadlineUnderlinedText}
                                    color={graphicsHeadlineColor}
                                />
                            ) : (
                                <div className={`S18HeadlineContainer ${HeadlineColorsMap[graphicsHeadlineColor]}`}>
                                    <div className="S18HeadlineText">
                                        {mobileUnderlinedLines.map((line) => (
                                            <div key={line} className="S18HeadlineUnderlined">
                                                {line}
                                            </div>
                                        ))}
                                        {headlineSuffix && <span>{headlineSuffix}</span>}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col items-center gap-8">
                            {/* No 1 nation provider */}
                            {!textAboveHeadline && textContent}
                            {/* CTA Button*/}
                            <Button onClick={() => buttonUrl && window?.open(buttonUrl, '_self')} variant={variant}>
                                {children}
                            </Button>
                        </div>

                        {/* Link Text */}
                        {link && (
                            <LinkWrapper
                                href={link}
                                color={linkColor}
                                target={'_self'}
                                className="hover:underline active:underline"
                            >
                                <Typography tag="p" color={linkColor} className="m-0">
                                    {linkText}
                                </Typography>
                            </LinkWrapper>
                        )}
                    </div>
                    {/* Overlay Content End */}
                </div>
                {/* Badge Image */}
                {videoLoaded && (
                    <div className="absolute lg:-bottom-16 lg:-left-32">
                        <div className="relative z-20">
                            <VideoBadge badgeImageSrc={imageSrcBadge} badgeImageAlt={imageAltBadge} />
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};
export default S18VideoBackground;
